import React from "react";
import { Link } from "react-router-dom";
import { TIDS } from "@/constants/testIds";
import { Users } from "lucide-react";

// Deterministic fallback covers when a streamer has NO custom thumbnail
// AND NO profile avatar. Picked by hashing the username so the same channel
// always gets the same fallback (no card-shuffling on re-fetch).
const COVER_IMGS = [
  "https://images.unsplash.com/photo-1616588589676-62b3bd4ff6d2",
  "https://images.unsplash.com/photo-1594009375825-564aac98bda6",
  "https://images.pexels.com/photos/1553831/pexels-photo-1553831.jpeg",
  "https://images.unsplash.com/photo-1622975696911-c1c444035f43",
  "https://images.unsplash.com/photo-1601042879364-f3947d3f9c16",
];

function hashCode(s = "") {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export default function StreamCard({ channel, index = 0 }) {
  // Priority: streamer's custom stream thumbnail → their profile avatar →
  // deterministic fallback keyed off the username so the card is stable.
  const cover =
    channel.thumbnail_url ||
    channel.avatar_url ||
    COVER_IMGS[hashCode(channel.username || String(index)) % COVER_IMGS.length];
  const isCustom = !!(channel.thumbnail_url || channel.avatar_url);
  return (
    <Link
      data-testid={TIDS.streamCard}
      to={`/c/${channel.username}/live`}
      className="sc-card overflow-hidden block group"
    >
      <div className="relative aspect-video bg-black overflow-hidden">
        {/* When the thumbnail is a portrait avatar rather than a landscape
            promo image, we blur-fill the sides so the aspect-video frame
            stays clean without letterboxing. */}
        {isCustom && (
          <img
            src={cover}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover scale-125 blur-xl opacity-40"
            loading="lazy"
            decoding="async"
          />
        )}
        <img
          src={cover}
          alt={channel.title || `${channel.username}'s stream`}
          className={`relative w-full h-full ${isCustom ? "object-contain" : "object-cover"} group-hover:scale-105 transition-transform duration-500`}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            // If the streamer-provided URL fails (dead link, 403 hotlink),
            // fall back to a deterministic default so the card never breaks.
            if (e.currentTarget.src !== COVER_IMGS[0]) {
              e.currentTarget.src = COVER_IMGS[hashCode(channel.username || String(index)) % COVER_IMGS.length];
              e.currentTarget.className = e.currentTarget.className.replace("object-contain", "object-cover");
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
        {channel.is_live && (
          <div className="absolute top-3 left-3">
            <span className="live-ribbon"><span className="live-dot" /> LIVE</span>
          </div>
        )}
        <div className="absolute top-3 right-3 coin-pill text-[10px]">
          <Users className="w-3 h-3" />
          <span>{channel.viewer_count || 0}</span>
        </div>
        {channel.category && (
          <div className="absolute bottom-3 left-3 text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-1 bg-black/60 backdrop-blur border border-white/10 rounded-sm sc-mono">
            {channel.category}
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="sc-heading text-base font-bold truncate group-hover:text-[color:var(--sc-pink)] transition">{channel.title}</div>
        <div className="text-xs text-zinc-500 mt-1 sc-mono">@{channel.username}</div>
      </div>
    </Link>
  );
}
